import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateChildFn, Router, UrlTree } from '@angular/router';
import { ConsentFormService } from '@consent';
import { ROUTES } from '@constants/routes.enums';
import { Observable, filter, map, take } from 'rxjs';

/**
 * Consent navigation gate (child guard on the authenticated shell). The consent
 * dialog is shown ON the dashboard by {@link PagesComponent}; this guard keeps a
 * user who still owes consent ON the dashboard until they accept — any attempt
 * to reach a deeper route (including programmatic/automation navigation that the
 * mandatory modal can't block) is redirected back to the dashboard.
 *
 * It deliberately does NOT use `canMatch`/`canActivate` on the shell route:
 * those suspend navigation, which would render the dialog over the *previous*
 * view (the login screen) instead of the dashboard. A single `canActivateChild`
 * here covers every current and future deep route from one place.
 *
 * The deep-route decision AWAITS consent resolution rather than reading the
 * signals synchronously. The synchronous read failed open during the brief
 * status-resolution window (before the status call returns, `required` is still
 * false) — exactly the gap fast programmatic/automation navigation slipped
 * through. By awaiting, deep navigation is suspended until the status is known,
 * then allowed (granted / not required / fail-open) or redirected to the
 * dashboard (required and not yet granted). The dashboard itself is always
 * allowed immediately so the shell can mount and the dialog renders over it.
 */
export const ConsentGuard: CanActivateChildFn = (
  _childRoute,
  state
): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const consent = inject(ConsentFormService);
  const router = inject(Router);

  // Fast paths — no await needed.
  // Already satisfied this session → let navigation through immediately.
  if (consent.granted()) {
    return true;
  }
  // The dashboard hosts the consent dialog: always allow it so the shell mounts
  // and the dialog shows over it (never suspended over the login screen).
  if (state.url.startsWith(ROUTES.DASHBOARD)) {
    return true;
  }

  // Deeper route while consent isn't yet granted. The guard runs BEFORE the
  // shell component is instantiated, so PagesComponent's own trigger hasn't
  // fired yet — kick off the (idempotent, shared) resolution ourselves.
  consent.ensureGranted().pipe(take(1)).subscribe();

  // Wait until the consent status has actually settled — granted, confirmed
  // required, or fully resolved (covers fail-open: resolved with neither flag).
  const settled = computed(
    () => consent.granted() || consent.required() || consent.resolved()
  );

  return toObservable(settled).pipe(
    filter(Boolean),
    take(1),
    map(() =>
      // Redirect to the dashboard only when consent is genuinely owed and not
      // yet granted; otherwise (granted, not required, or fail-open) allow.
      consent.required() && !consent.granted()
        ? router.parseUrl(ROUTES.DASHBOARD)
        : true
    )
  );
};
